/**
 * ContentSlot behavior — a container that fits its one child (#950)
 *
 * Data only: the fit itself is layout's. When the node also carries a
 * `Layout`, the solver reads {@link ContentSlot.layoutContentFit} and scales
 * the container's single flow child to the container's width and height (see
 * `ContentFitSource` in layout.ts and LAYOUT_ENGINE.md §5.2). Without a
 * `Layout` it does nothing.
 *
 * Wrappers use it: the slot inside a placed wrapper copy is a plain container
 * with a `ContentSlot`, and whatever the scene puts in it is the content.
 */

import { TraceBehavior } from "./tracebehavior";
import { invalidateNearestLayout } from "./layout";
import type { ContentFitSource } from "./layout";
import type { ContentSlotData, ContentSlotFit } from "../scenedata/behaviors/contentslotdata";
import type { ValidationError } from "../scenedata/behaviordata";

export class ContentSlot extends TraceBehavior<ContentSlotData> implements ContentFitSource {
    static readonly behaviorType = "ContentSlot" as const;

    /** The fit layout applies to this container's child. */
    get layoutContentFit(): { fit: ContentSlotFit; zooms?: number[] } {
        const fit = this._resolved.fit ?? 'contain';
        const zooms = this._resolved.zooms;
        return zooms && zooms.length > 0 ? { fit, zooms: [...zooms] } : { fit };
    }

    /**
     * Same two-path contract as `Model.validate`: an out-of-vocabulary value
     * is dropped so a loaded scene stays editable, and reported so a commit
     * carrying it is rejected.
     */
    protected validate(data: ContentSlotData): ValidationError[] {
        const fit = data.fit as unknown;
        if (fit !== undefined && fit !== 'contain' && fit !== 'scale-down' && fit !== 'none') {
            delete data.fit;
            return [{
                field: 'fit',
                message: `fit must be 'contain', 'scale-down' or 'none'`,
                code: 'INVALID_FIT',
            }];
        }
        const zooms = data.zooms as unknown;
        if (zooms !== undefined
            && !(Array.isArray(zooms) && zooms.every(z => typeof z === 'number' && Number.isFinite(z) && z > 0))) {
            delete data.zooms;
            return [{
                field: 'zooms',
                message: 'zooms must be a list of positive numbers',
                code: 'INVALID_ZOOMS',
            }];
        }
        return [];
    }

    protected onAttach(): void {
        invalidateNearestLayout(this.sceneObject);
    }

    protected onDetach(): void {
        invalidateNearestLayout(this.sceneObject);
    }

    protected async applyCommit(): Promise<void> {
        invalidateNearestLayout(this.sceneObject);
    }
}
